import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled, { css } from 'styled-components';
import {
  ArrowLeft,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Copy,
  Check,
  Pencil,
  ExternalLink,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';

import { Button } from '@auw/workspace-ui';
import { Checkbox } from '@auw/workspace-ui';
import { Chip } from '@auw/workspace-ui';
import { Input } from '@auw/workspace-ui';
import { Modal } from '@auw/workspace-ui';
import { RingSpinner } from '@auw/workspace-ui';
import { Textarea } from '@auw/workspace-ui';
import { Tooltip } from '@auw/workspace-ui';
import { useChatStore } from '@auw/stores';
import { useWorkspaceNavigate } from '@auw/workspace-features';
import {
  queryKeys,
  buildPluginOAuthRedirectUri,
  clearPendingPluginOAuthFlow,
  createPluginOAuthFlowId,
  setPendingPluginOAuthFlow,
  getPluginOAuthChannel,
  getPluginOAuthPopupName,
  type PluginOAuthResultMessage,
} from '@auw/workspace-utils';
import { MODAL_GC_TIME, MODAL_STALE_TIME } from '@auw/workspace-ui';
import { useTenant } from '@auw/tenant';
import {
  listCalendarEvents,
  listCalendarProviders,
  createCalendarEvents,
  searchCalendarGuests,
  updateCalendarEvent,
  updateCalendarProviderEvent,
  deleteCalendarEvent,
  deleteCalendarProviderEvent,
  getGoogleCalendarOAuthUrl,
  getOutlookCalendarOAuthUrl,
  type CalendarGuest,
  type CalendarGuestSuggestion,
  type CalendarGuestSuggestionProviderError,
  type CalendarInviteSender,
  type CalendarEventItem,
  type MailProviderId,
  type CalendarProviderId,
  type CalendarProviderInfo,
  type ConferenceProvider,
} from '@auw/api-client';

type LayoutMode = 'wide' | 'medium' | 'compact';
type CompactView = 'month' | 'agenda' | 'composer';

interface CalendarDraft {
  title: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  location: string;
  description: string;
  guests: CalendarGuest[];
  sendInviteEmail: boolean;
  inviteSenderProvider: MailProviderId | null;
  targetProviders: CalendarProviderId[];
  conferenceProvider: ConferenceProvider;
}

interface CalendarDayPopoverPosition {
  top: number;
  left: number;
  width: number;
}

interface GuestSuggestionState {
  suggestions: CalendarGuestSuggestion[];
  senders: CalendarInviteSender[];
  provider_errors: CalendarGuestSuggestionProviderError[];
}

interface CalendarWidgetModalProps {
  open: boolean;
  onClose: () => void;
  selectedDate: string;
  selectedProviders: CalendarProviderId[];
  onSelectedDateChange?: (date: string) => void;
  onSelectedProvidersChange?: (providers: CalendarProviderId[]) => void;
  modalDraggable?: boolean;
  modalPinnable?: boolean;
  modalDefaultPinned?: boolean;
  modalInitialOffsetX?: number;
  modalInitialOffsetY?: number;
  modalOpacityAdjustable?: boolean;
  modalDefaultOpacity?: number;
  modalDefaultWidth?: number;
  modalDefaultHeight?: number;
  modalMinWidth?: number;
  modalMinHeight?: number;
  modalCloseOnBackdrop?: boolean;
  modalDimBackdrop?: boolean;
  modalPassthroughBackdrop?: boolean;
  modalCenterPaddingLeft?: number;
  modalCenterPaddingRight?: number;
  mountMode?: 'viewport' | 'parent';
  hostBounds?: { width: number; height: number };
  modalOnSizeChange?: (size: { w: number; h: number }) => void;
  modalOnOffsetChange?: (offset: { x: number; y: number }) => void;
  modalOnPinnedChange?: (pinned: boolean) => void;
  modalOnOpacityChange?: (opacity: number) => void;
  modalLayerZIndex?: number;
  modalOnActivate?: () => void;
}

const PROVIDER_META: Record<CalendarProviderId, { label: string; icon: string }> = {
  widget_calendar: { label: 'Calendar', icon: '/app_name.png' },
  google_calendar: { label: 'Google', icon: '/logo/google_calendar.webp' },
  outlook_calendar: { label: 'Outlook', icon: '/logo/outlook.webp' },
  zoom: { label: 'Zoom', icon: '/logo/zoom.webp' },
};

const CONFERENCE_OPTIONS: Array<{
  id: ConferenceProvider;
  label: string;
  icon: string;
  requiredProvider: CalendarProviderId | null;
}> = [
  { id: 'none', label: 'none', icon: '', requiredProvider: null },
  { id: 'google_meet', label: 'Google Meet', icon: '/logo/google_calendar.webp', requiredProvider: 'google_calendar' },
  { id: 'teams', label: 'Teams', icon: '/logo/outlook.webp', requiredProvider: 'outlook_calendar' },
  { id: 'zoom', label: 'Zoom', icon: '/logo/zoom.webp', requiredProvider: 'zoom' },
];

const WEEKDAY_LABELS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const GUEST_SUGGESTION_LIMIT = 8;
const GUEST_RECENT_SUGGESTION_LIMIT = 12;
const GUEST_REMOTE_SEARCH_MIN_LENGTH = 2;
const HTML_DETECTION_RE = /<\/?[a-z][\s\S]*>/i;
const HTML_COMMENT_RE = /<!--[\s\S]*?-->/g;
const HTML_SCRIPT_STYLE_RE = /<(script|style)\b[\s\S]*?<\/\1>/gi;
const HTML_BREAK_RE = /<br\s*\/?>|<\/(?:p|div|li|tr|table|blockquote|h[1-6])>/gi;
const HTML_TAG_RE = /<[^>]+>/g;
const GUEST_SUGGESTION_PLUGIN_LABELS: Record<string, string> = {
  google_calendar: 'Google Calendar',
  outlook: 'Outlook Mail',
};
const HIDEABLE_CALENDAR_PROVIDER_IDS: readonly CalendarProviderId[] = [
  'google_calendar',
  'outlook_calendar',
  'zoom',
];

const hideScrollbarStyles = css`
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`;

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function parseDateKey(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function startOfWeek(date: Date): Date {
  return addDays(date, -date.getDay());
}

function endOfWeek(date: Date): Date {
  return addDays(startOfWeek(date), 6);
}

function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function createMonthCells(anchorMonth: Date): Date[] {
  const start = startOfWeek(startOfMonth(anchorMonth));
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}


function toQueryIso(date: Date, endOfDay: boolean): string {
  const copy = new Date(date);
  if (endOfDay) {
    copy.setHours(23, 59, 59, 999);
  } else {
    copy.setHours(0, 0, 0, 0);
  }
  return copy.toISOString();
}

function parseEventDate(value: string): Date | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parseDateKey(value);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function enumerateEventDateKeys(event: CalendarEventItem): string[] {
  const start = parseEventDate(event.start);
  const end = parseEventDate(event.end);
  if (!start) return [];

  const last = end ? new Date(end) : new Date(start);
  if (event.all_day) {
    last.setDate(last.getDate() - 1);
  }
  if (last < start) last.setTime(start.getTime());

  const keys: string[] = [];
  let cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const limit = new Date(last.getFullYear(), last.getMonth(), last.getDate());
  while (cursor <= limit) {
    keys.push(toDateKey(cursor));
    cursor = addDays(cursor, 1);
  }
  return keys;
}

function toTimeInput(date: Date | null, fallback: string): string {
  if (!date) return fallback;
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function normalizeGuest(guest: CalendarGuest): CalendarGuest | null {
  const email = guest.email.trim().toLowerCase();
  if (!isValidEmail(email)) return null;
  const name = guest.name?.trim() || undefined;
  return { email, name };
}

function dedupeGuests(guests: CalendarGuest[]): CalendarGuest[] {
  const seen = new Set<string>();
  const out: CalendarGuest[] = [];
  guests.forEach((guest) => {
    const normalized = normalizeGuest(guest);
    if (!normalized || seen.has(normalized.email)) return;
    seen.add(normalized.email);
    out.push(normalized);
  });
  return out;
}

function guestLabel(guest: CalendarGuest): string {
  return guest.name ? `${guest.name} <${guest.email}>` : guest.email;
}

function getMutableProviderSource(
  event: CalendarEventItem,
): { provider: Exclude<CalendarProviderId, 'widget_calendar'>; externalId: string } | null {
  const source = event.sources[0];
  if (!source) return null;
  if (source.provider === 'widget_calendar') return null;
  return {
    provider: source.provider as Exclude<CalendarProviderId, 'widget_calendar'>,
    externalId: source.external_id,
  };
}

function defaultDraft(dateKey: string, providers: CalendarProviderId[]): CalendarDraft {
  return {
    title: '',
    startDate: dateKey,
    endDate: dateKey,
    startTime: '09:00',
    endTime: '10:00',
    allDay: false,
    location: '',
    description: '',
    guests: [],
    sendInviteEmail: false,
    inviteSenderProvider: null,
    targetProviders: providers,
    conferenceProvider: 'none',
  };
}

function isProviderViewable(provider: CalendarProviderInfo): boolean {
  return provider.connected && provider.can_view;
}

function isProviderCreatable(provider: CalendarProviderInfo): boolean {
  return provider.connected && provider.can_create;
}

function sanitizeProviders(
  providers: CalendarProviderId[],
  providerInfos: CalendarProviderInfo[],
  capability: 'view' | 'create' = 'view',
): CalendarProviderId[] {
  const allowed = new Set(
    providerInfos
      .filter((item) => (capability === 'create' ? isProviderCreatable(item) : isProviderViewable(item)))
      .map((item) => item.id),
  );
  return providers.filter((item, index) => allowed.has(item) && providers.indexOf(item) === index);
}

function summarizeCreateResult(results: Array<{ success: boolean }>): { successCount: number; failCount: number } {
  return results.reduce(
    (acc, item) => {
      if (item.success) acc.successCount += 1;
      else acc.failCount += 1;
      return acc;
    },
    { successCount: 0, failCount: 0 },
  );
}

function decodeHtmlEntities(value: string): string {
  if (typeof document === 'undefined') {
    return value
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'");
  }
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function normalizeCalendarText(value: string | null | undefined): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const looksLikeHtml = /^<!doctype html/i.test(raw) || /<html[\s>]/i.test(raw) || HTML_DETECTION_RE.test(raw);
  if (!looksLikeHtml) return raw;

  const stripped = raw
    .replace(HTML_COMMENT_RE, ' ')
    .replace(HTML_SCRIPT_STYLE_RE, ' ')
    .replace(HTML_BREAK_RE, '\n')
    .replace(HTML_TAG_RE, ' ');
  const decoded = decodeHtmlEntities(stripped).replace(/\u00a0/g, ' ');
  return decoded
    .split('\n')
    .map((line) => line.replace(/[ \t\r\f\v]+/g, ' ').trim())
    .filter(Boolean)
    .join('\n')
    .trim();
}

function draftFromEvent(event: CalendarEventItem): CalendarDraft {
  const start = parseEventDate(event.start);
  const end = parseEventDate(event.end);
  const startDate = start ? toDateKey(start) : toDateKey(new Date());
  const endDate = (() => {
    if (!end) return startDate;
    if (event.all_day) {
      return toDateKey(addDays(end, -1));
    }
    return toDateKey(end);
  })();

  return {
    title: event.title,
    startDate,
    endDate,
    startTime: toTimeInput(start, '09:00'),
    endTime: toTimeInput(end, '10:00'),
    allDay: event.all_day,
    location: event.location || '',
    description: normalizeCalendarText(event.description) || '',
    guests: event.guests || [],
    sendInviteEmail: false,
    inviteSenderProvider: null,
    targetProviders: event.sources.map((source) => source.provider),
    conferenceProvider: 'none',
  };
}

export const CalendarWidgetModal: React.FC<CalendarWidgetModalProps> = ({
  open,
  onClose,
  selectedDate,
  selectedProviders,
  onSelectedDateChange,
  onSelectedProvidersChange,
  modalDraggable = false,
  modalPinnable = false,
  modalDefaultPinned = false,
  modalInitialOffsetX = 0,
  modalInitialOffsetY = 0,
  modalOpacityAdjustable = false,
  modalDefaultOpacity = 1,
  modalDefaultWidth = 1080,
  modalDefaultHeight = 720,
  modalMinWidth = 560,
  modalMinHeight = 520,
  modalCloseOnBackdrop = true,
  modalDimBackdrop = true,
  modalPassthroughBackdrop = false,
  modalCenterPaddingLeft = 0,
  modalCenterPaddingRight = 0,
  mountMode = 'viewport',
  hostBounds,
  modalOnSizeChange,
  modalOnOffsetChange,
  modalOnPinnedChange,
  modalOnOpacityChange,
  modalLayerZIndex,
  modalOnActivate,
}) => {
  const addToast = useChatStore((s) => s.addToast);
  const { t, i18n } = useTranslation('widget');
  const { openPluginDetail } = useWorkspaceNavigate();
  const queryClient = useQueryClient();
  const tenant = useTenant();
  const layoutRef = useRef<HTMLDivElement>(null);
  const calendarCardRef = useRef<HTMLDivElement>(null);
  const monthGridRef = useRef<HTMLDivElement>(null);
  const guestPickerRef = useRef<HTMLDivElement>(null);
  const dayCellRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const initializedProvidersRef = useRef(false);
  const wasOpenRef = useRef(false);

  const safeSelectedDate = useMemo(
    () => (/^\d{4}-\d{2}-\d{2}$/.test(selectedDate) ? selectedDate : toDateKey(new Date())),
    [selectedDate],
  );
  const [monthCursor, setMonthCursor] = useState<Date>(() => startOfMonth(parseDateKey(safeSelectedDate)));
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('wide');
  const [compactView, setCompactView] = useState<CompactView>('month');
  const [composerOpen, setComposerOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [copiedEventId, setCopiedEventId] = useState<string | null>(null);
  const [editingEvent, setEditingEvent] = useState<CalendarEventItem | null>(null);
  const [guestQuery, setGuestQuery] = useState('');
  const [debouncedGuestQuery, setDebouncedGuestQuery] = useState('');
  const [guestDropdownOpen, setGuestDropdownOpen] = useState(false);
  const [draft, setDraft] = useState<CalendarDraft>(() => defaultDraft(safeSelectedDate, []));
  const surfaceOpacity = Math.min(1, Math.max(0.35, modalDefaultOpacity));
  const [hoveredDateKey, setHoveredDateKey] = useState<string | null>(null);
  const [pinnedPreviewDateKey, setPinnedPreviewDateKey] = useState<string | null>(null);
  const [dayPopoverPosition, setDayPopoverPosition] = useState<CalendarDayPopoverPosition | null>(null);
  const [connectingProviderId, setConnectingProviderId] = useState<CalendarProviderId | null>(null);

  const providersQuery = useQuery({
    queryKey: queryKeys.calendar.providers(),
    queryFn: listCalendarProviders,
    enabled: open,
    refetchOnWindowFocus: true,
  });

  const hiddenCalendarProviderIds = useMemo(() => {
    const hiddenPluginIds = tenant.plugins?.hiddenIds ?? [];
    return new Set<CalendarProviderId>(
      hiddenPluginIds.filter((id): id is CalendarProviderId =>
        HIDEABLE_CALENDAR_PROVIDER_IDS.includes(id as CalendarProviderId),
      ),
    );
  }, [tenant.plugins?.hiddenIds]);
  const availableConferenceOptions = useMemo(
    () => CONFERENCE_OPTIONS.filter(
      (option) => !option.requiredProvider || !hiddenCalendarProviderIds.has(option.requiredProvider),
    ),
    [hiddenCalendarProviderIds],
  );
  const rawProviderInfos = providersQuery.data?.providers ?? [];
  const providerInfos = useMemo(
    () => rawProviderInfos.filter((provider) => !hiddenCalendarProviderIds.has(provider.id)),
    [rawProviderInfos, hiddenCalendarProviderIds],
  );
  const effectiveSelectedProviders = sanitizeProviders(selectedProviders, providerInfos, 'view');
  const viewableProviderIds = providerInfos.filter(isProviderViewable).map((item) => item.id);
  const creatableProviderIds = providerInfos.filter(isProviderCreatable).map((item) => item.id);
  const preferredDraftTargets = useMemo(() => {
    const selectedCreatable = sanitizeProviders(effectiveSelectedProviders, providerInfos, 'create');
    return selectedCreatable.length > 0 ? selectedCreatable : creatableProviderIds;
  }, [effectiveSelectedProviders, providerInfos, creatableProviderIds]);
  const shouldSearchGuestsRemotely = debouncedGuestQuery.length >= GUEST_REMOTE_SEARCH_MIN_LENGTH;

  const recentGuestSuggestionsQuery = useQuery({
    queryKey: queryKeys.calendar.guestSuggestions('', GUEST_RECENT_SUGGESTION_LIMIT),
    queryFn: () => searchCalendarGuests('', GUEST_RECENT_SUGGESTION_LIMIT),
    enabled: open && composerOpen,
    staleTime: MODAL_STALE_TIME,
    gcTime: MODAL_GC_TIME,
    refetchOnWindowFocus: false,
  });
  const searchedGuestSuggestionsQuery = useQuery({
    queryKey: queryKeys.calendar.guestSuggestions(debouncedGuestQuery, GUEST_SUGGESTION_LIMIT),
    queryFn: () => searchCalendarGuests(debouncedGuestQuery, GUEST_SUGGESTION_LIMIT),
    enabled: open && composerOpen && shouldSearchGuestsRemotely,
    staleTime: MODAL_STALE_TIME,
    gcTime: MODAL_GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
  const recentGuestSuggestionState: GuestSuggestionState = recentGuestSuggestionsQuery.data ?? {
    suggestions: [],
    senders: [],
    provider_errors: [],
  };
  const inviteSenders = (searchedGuestSuggestionsQuery.data?.senders?.length
    ? searchedGuestSuggestionsQuery.data.senders
    : recentGuestSuggestionState.senders);
  const needsTemplateInviteSender = useMemo(() => {
    if (!editingEvent) {
      return !draft.targetProviders.some((provider) => provider === 'google_calendar' || provider === 'outlook_calendar');
    }
    if (editingEvent.group_id) {
      return !editingEvent.sources.some(
        (source) => source.provider === 'google_calendar' || source.provider === 'outlook_calendar',
      );
    }
    return editingEvent.provider !== 'google_calendar' && editingEvent.provider !== 'outlook_calendar';
  }, [draft.targetProviders, editingEvent]);
  const hasOutlookGuestSyncTarget = useMemo(() => {
    if (!editingEvent) {
      return draft.targetProviders.includes('outlook_calendar');
    }
    if (editingEvent.group_id) {
      return editingEvent.sources.some((source) => source.provider === 'outlook_calendar');
    }
    return editingEvent.provider === 'outlook_calendar';
  }, [draft.targetProviders, editingEvent]);
  const guestQueryValue = guestQuery.trim().toLowerCase();
  const localGuestSuggestions = useMemo(() => {
    if (!guestQueryValue) return recentGuestSuggestionState.suggestions;
    return recentGuestSuggestionState.suggestions.filter((suggestion) => {
      const searchable = `${suggestion.name || ''} ${suggestion.email}`.trim().toLowerCase();
      return searchable.includes(guestQueryValue);
    });
  }, [guestQueryValue, recentGuestSuggestionState.suggestions]);
  const rawGuestSuggestions = shouldSearchGuestsRemotely
    ? (searchedGuestSuggestionsQuery.data?.suggestions ?? localGuestSuggestions)
    : localGuestSuggestions;
  const guestSuggestions = rawGuestSuggestions.filter(
    (suggestion) => !draft.guests.some((guest) => guest.email.toLowerCase() === suggestion.email.toLowerCase()),
  );
  const guestSuggestionProviderErrors = useMemo(() => {
    const rawErrors = shouldSearchGuestsRemotely
      ? (searchedGuestSuggestionsQuery.data?.provider_errors ?? recentGuestSuggestionState.provider_errors)
      : recentGuestSuggestionState.provider_errors;
    const seen = new Set<string>();
    return rawErrors.filter((item) => {
      const key = `${item.plugin_id}:${item.message || ''}:${item.reauth_required ? '1' : '0'}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [shouldSearchGuestsRemotely, searchedGuestSuggestionsQuery.data?.provider_errors, recentGuestSuggestionState.provider_errors]);
  const guestSuggestionsLoading = shouldSearchGuestsRemotely
    ? (searchedGuestSuggestionsQuery.isFetching && guestSuggestions.length === 0)
    : (recentGuestSuggestionsQuery.isFetching && guestSuggestions.length === 0);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    if (justOpened) {
      setMonthCursor(startOfMonth(parseDateKey(safeSelectedDate)));
      setCompactView('month');
      setComposerOpen(false);
      setEditingEvent(null);
      setGuestQuery('');
      setDebouncedGuestQuery('');
      setGuestDropdownOpen(false);
      setHoveredDateKey(null);
      setPinnedPreviewDateKey(null);
    }
    if (!open) {
      initializedProvidersRef.current = false;
      setEditingEvent(null);
      setGuestQuery('');
      setDebouncedGuestQuery('');
      setGuestDropdownOpen(false);
      setHoveredDateKey(null);
      setPinnedPreviewDateKey(null);
      setDayPopoverPosition(null);
    }
    wasOpenRef.current = open;
  }, [open, safeSelectedDate]);

  useEffect(() => {
    if (!open || providerInfos.length === 0) return;
    const sanitized = sanitizeProviders(selectedProviders, providerInfos, 'view');
    if (sanitized.length !== selectedProviders.length) {
      onSelectedProvidersChange?.(sanitized);
      return;
    }
    if (!initializedProvidersRef.current) {
      initializedProvidersRef.current = true;
      if (sanitized.length === 0 && viewableProviderIds.length > 0) {
        onSelectedProvidersChange?.(viewableProviderIds);
      }
    }
  }, [open, providerInfos, selectedProviders, viewableProviderIds, onSelectedProvidersChange]);

  useEffect(() => {
    if (!open) return;
    void queryClient.prefetchQuery({
      queryKey: queryKeys.calendar.guestSuggestions('', GUEST_RECENT_SUGGESTION_LIMIT),
      queryFn: () => searchCalendarGuests('', GUEST_RECENT_SUGGESTION_LIMIT),
      staleTime: MODAL_STALE_TIME,
      gcTime: MODAL_GC_TIME,
    });
  }, [open, queryClient]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedGuestQuery(guestQuery.trim());
    }, 220);
    return () => window.clearTimeout(timer);
  }, [guestQuery]);

  useEffect(() => {
    if (!draft.sendInviteEmail) return;
    if (inviteSenders.length === 1 && !draft.inviteSenderProvider) {
      setDraft((prev) => ({ ...prev, inviteSenderProvider: inviteSenders[0].provider }));
      return;
    }
    if (
      draft.inviteSenderProvider
      && !inviteSenders.some((sender) => sender.provider === draft.inviteSenderProvider)
    ) {
      setDraft((prev) => ({ ...prev, inviteSenderProvider: null }));
    }
  }, [draft.sendInviteEmail, draft.inviteSenderProvider, inviteSenders]);

  useEffect(() => {
    if (!guestDropdownOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (!guestPickerRef.current?.contains(event.target as Node)) {
        setGuestDropdownOpen(false);
      }
    };
    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [guestDropdownOpen]);

  useEffect(() => {
    if (!open) return;
    const el = layoutRef.current;
    if (!el) return;

    const updateLayout = () => {
      const width = el.clientWidth;
      if (width <= 760) setLayoutMode('compact');
      else if (width <= 1040) setLayoutMode('medium');
      else setLayoutMode('wide');
    };

    updateLayout();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateLayout);
      return () => window.removeEventListener('resize', updateLayout);
    }

    const observer = new ResizeObserver(updateLayout);
    observer.observe(el);
    return () => observer.disconnect();
  }, [open]);

  useEffect(() => {
    if (layoutMode !== 'compact' && compactView === 'agenda') {
      setCompactView(composerOpen ? 'composer' : 'month');
    }
  }, [layoutMode, compactView, composerOpen]);

  useEffect(() => {
    if (!open) return;
    setDraft((prev) => {
      const nextTargets = sanitizeProviders(prev.targetProviders, providerInfos, 'create');
      const currentConference = CONFERENCE_OPTIONS.find((option) => option.id === prev.conferenceProvider);
      const nextConferenceProvider = currentConference?.requiredProvider
        && hiddenCalendarProviderIds.has(currentConference.requiredProvider)
          ? 'none'
          : prev.conferenceProvider;
      const sameTargets = nextTargets.length === prev.targetProviders.length
        && nextTargets.every((item, index) => item === prev.targetProviders[index]);
      if (
        prev.startDate === safeSelectedDate
        && sameTargets
        && nextConferenceProvider === prev.conferenceProvider
      ) {
        return prev;
      }
      return {
        ...prev,
        startDate: safeSelectedDate,
        endDate: safeSelectedDate,
        targetProviders: nextTargets.length > 0 ? nextTargets : preferredDraftTargets,
        conferenceProvider: nextConferenceProvider,
      };
    });
  }, [open, safeSelectedDate, preferredDraftTargets, providerInfos, hiddenCalendarProviderIds]);

  const monthCells = useMemo(() => createMonthCells(monthCursor), [monthCursor]);
  const monthRange = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(monthCursor));
    const gridEnd = endOfWeek(endOfMonth(monthCursor));
    return {
      start: toQueryIso(gridStart, false),
      end: toQueryIso(gridEnd, true),
      key: `${toDateKey(gridStart)}:${toDateKey(gridEnd)}`,
    };
  }, [monthCursor]);
  const shouldFetchAllProviders = !initializedProvidersRef.current && effectiveSelectedProviders.length === 0;
  const shouldFetchEvents = open;
  const requestedProviders = shouldFetchAllProviders ? undefined : effectiveSelectedProviders;

  const eventsQuery = useQuery({
    queryKey: queryKeys.calendar.events(
      monthRange.key,
      requestedProviders === undefined
        ? 'all'
        : requestedProviders.length > 0
          ? requestedProviders.slice().sort().join(',')
          : 'none',
    ),
    queryFn: () => listCalendarEvents({
      start: monthRange.start,
      end: monthRange.end,
      providers: requestedProviders,
    }),
    enabled: shouldFetchEvents,
    refetchOnWindowFocus: true,
  });

  const events = shouldFetchEvents ? (eventsQuery.data?.events ?? []) : [];
  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEventItem[]>();
    for (const event of events) {
      for (const key of enumerateEventDateKeys(event)) {
        const bucket = map.get(key);
        if (bucket) bucket.push(event);
        else map.set(key, [event]);
      }
    }
    for (const bucket of map.values()) {
      bucket.sort((a, b) => {
        if (a.all_day !== b.all_day) return a.all_day ? -1 : 1;
        return a.start.localeCompare(b.start);
      });
    }
    return map;
  }, [events]);

  const selectedDayEvents = useMemo(
    () => [...(eventsByDate.get(safeSelectedDate) ?? [])],
    [eventsByDate, safeSelectedDate],
  );
  const activeDesktopPreviewDateKey = (
    layoutMode === 'compact' || composerOpen
      ? null
      : (pinnedPreviewDateKey ?? hoveredDateKey)
  );

  const updateDayPopoverPosition = useCallback((dateKey: string | null) => {
    if (!dateKey) {
      setDayPopoverPosition(null);
      return;
    }
    const card = calendarCardRef.current;
    const cell = dayCellRefs.current[dateKey];
    if (!card || !cell) {
      setDayPopoverPosition(null);
      return;
    }

    const cardRect = card.getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    const width = Math.min(300, Math.max(240, window.innerWidth - 96));
    const gap = 12;
    const maxLeft = Math.max(12, cardRect.width - width - 12);

    let left = cellRect.right - cardRect.left + gap;
    if (left > maxLeft) {
      left = cellRect.left - cardRect.left - width - gap;
    }
    left = Math.min(maxLeft, Math.max(12, left));

    const preferredHeight = 320;
    const maxTop = Math.max(12, cardRect.height - preferredHeight - 12);
    const top = Math.min(maxTop, Math.max(12, cellRect.top - cardRect.top));

    setDayPopoverPosition({ top, left, width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateDayPopoverPosition(activeDesktopPreviewDateKey);
    if (!activeDesktopPreviewDateKey) return;

    const handlePosition = () => updateDayPopoverPosition(activeDesktopPreviewDateKey);
    const grid = monthGridRef.current;
    window.addEventListener('resize', handlePosition);
    grid?.addEventListener('scroll', handlePosition, { passive: true });

    return () => {
      window.removeEventListener('resize', handlePosition);
      grid?.removeEventListener('scroll', handlePosition);
    };
  }, [open, activeDesktopPreviewDateKey, monthCursor, updateDayPopoverPosition]);

  const handleGoToPlugin = useCallback((pluginId?: string) => {
    const nextPluginId = pluginId ?? viewableProviderIds[0] ?? 'google_calendar';
    const providerLabel = PROVIDER_META[nextPluginId as CalendarProviderId]?.label ?? nextPluginId;
    addToast({
      type: 'info',
      title: t('calendar.navigatingToPlugin', { provider: providerLabel }),
    });
    openPluginDetail(nextPluginId);
  }, [viewableProviderIds, openPluginDetail, addToast, t]);

  /* In-place OAuth (re)connect for calendar providers. The widget is a
   * floating overlay, so navigating to the plugin page (the previous
   * behaviour) ejected the user from the canvas AND showed a misleading
   * "not connected" toast even when the account only needed re-auth. We run
   * the OAuth popup right here and refetch the provider list on completion. */
  const handleConnectCalendarProvider = useCallback((providerId: CalendarProviderId) => {
    if (providerId !== 'google_calendar' && providerId !== 'outlook_calendar') {
      handleGoToPlugin(providerId);
      return;
    }
    if (connectingProviderId) return;

    setConnectingProviderId(providerId);
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const features = `width=${width},height=${height},left=${left},top=${top},popup=1`;
    const popup = window.open('', '_blank', features);
    const flowId = createPluginOAuthFlowId();

    setPendingPluginOAuthFlow({
      plugin: providerId,
      flowId,
      popupExpected: Boolean(popup),
      startedAt: Date.now(),
    });

    if (popup) {
      popup.name = getPluginOAuthPopupName();
      try {
        popup.document.body.innerHTML =
          `<div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;padding:20px;text-align:center;color:#444;">${t('calendar.connectRedirecting')}</div>`;
      } catch {
        // 일부 브라우저에서는 즉시 DOM 접근이 실패할 수 있음
      }
    }

    void (async () => {
      try {
        const redirectUri = buildPluginOAuthRedirectUri(
          `${window.location.origin}${window.location.pathname}`,
          { flowId, popupExpected: Boolean(popup) },
        );
        const result = providerId === 'google_calendar'
          ? await getGoogleCalendarOAuthUrl(redirectUri)
          : await getOutlookCalendarOAuthUrl(redirectUri);

        if (popup && !popup.closed) {
          popup.location.href = result.authorization_url;
          const pollId = window.setInterval(() => {
            if (popup.closed) {
              window.clearInterval(pollId);
              clearPendingPluginOAuthFlow(flowId);
              setConnectingProviderId(null);
              void providersQuery.refetch();
            }
          }, 1000);
          window.setTimeout(() => {
            window.clearInterval(pollId);
            if (!popup.closed) popup.close();
            clearPendingPluginOAuthFlow(flowId);
            setConnectingProviderId(null);
          }, 5 * 60 * 1000);
        } else {
          // 팝업 차단 시 현재 탭에서 직접 이동 (fallback)
          window.location.href = result.authorization_url;
        }
      } catch (e: unknown) {
        if (popup && !popup.closed) popup.close();
        clearPendingPluginOAuthFlow(flowId);
        setConnectingProviderId(null);
        const providerLabel = PROVIDER_META[providerId]?.label ?? providerId;
        addToast({
          type: 'error',
          title: t('calendar.connectFailed', { provider: providerLabel }),
          description: e instanceof Error ? e.message : undefined,
        });
      }
    })();
  }, [connectingProviderId, handleGoToPlugin, providersQuery, addToast, t]);

  useEffect(() => {
    let ch: BroadcastChannel | null = null;
    const handler = (msg: PluginOAuthResultMessage) => {
      if (msg.plugin !== 'google_calendar' && msg.plugin !== 'outlook_calendar') return;
      setConnectingProviderId(null);
      void providersQuery.refetch();
      if (msg.status === 'connected') {
        const providerLabel = PROVIDER_META[msg.plugin as CalendarProviderId]?.label ?? msg.plugin;
        addToast({
          type: 'success',
          title: t('calendar.connectSuccess', { provider: providerLabel }),
          duration: 2000,
        });
      }
    };
    try {
      ch = new BroadcastChannel(getPluginOAuthChannel());
      ch.addEventListener('message', (e: MessageEvent<PluginOAuthResultMessage>) => handler(e.data));
    } catch {
      // BroadcastChannel 미지원 브라우저에서는 popup close 폴링으로 처리
    }
    return () => { ch?.close(); };
  }, [providersQuery, addToast, t]);

  const handleRefresh = useCallback(() => {
    void providersQuery.refetch();
    void eventsQuery.refetch();
  }, [providersQuery, eventsQuery]);

  const handleDateSelect = useCallback((dateKey: string) => {
    onSelectedDateChange?.(dateKey);
    const dayEvents = eventsByDate.get(dateKey) ?? [];
    if (dayEvents.length === 0) {
      setPinnedPreviewDateKey(null);
      setHoveredDateKey(null);
      setDayPopoverPosition(null);
      return;
    }
    if (layoutMode === 'compact') {
      setCompactView('agenda');
      setPinnedPreviewDateKey(null);
      setHoveredDateKey(null);
      setDayPopoverPosition(null);
      return;
    }
    if (pinnedPreviewDateKey === dateKey) {
      setPinnedPreviewDateKey(null);
      setHoveredDateKey(null);
      setDayPopoverPosition(null);
      return;
    }
    setPinnedPreviewDateKey(dateKey);
    setHoveredDateKey(dateKey);
    updateDayPopoverPosition(dateKey);
  }, [eventsByDate, layoutMode, onSelectedDateChange, pinnedPreviewDateKey, updateDayPopoverPosition]);

  const toggleProviderFilter = useCallback((providerId: CalendarProviderId) => {
    const provider = providerInfos.find((item) => item.id === providerId);
    if (!provider || !isProviderViewable(provider)) return;
    const current = sanitizeProviders(selectedProviders, providerInfos, 'view');
    const exists = current.includes(providerId);
    const next = exists
      ? current.filter((item) => item !== providerId)
      : [...current, providerId];
    onSelectedProvidersChange?.(next);
  }, [providerInfos, selectedProviders, onSelectedProvidersChange]);

  const i18nLocale = useMemo(() => {
    const lang = i18n.language;
    if (lang === 'ko') return 'ko-KR';
    if (lang === 'ja') return 'ja-JP';
    if (lang === 'zh-CN') return 'zh-CN';
    if (lang === 'zh-TW') return 'zh-TW';
    return 'en-US';
  }, [i18n.language]);

  const getProviderLabel = useCallback((providerId: CalendarProviderId): string => {
    if (providerId === 'widget_calendar') return t('calendar.title');
    return PROVIDER_META[providerId]?.label ?? providerId;
  }, [t]);

  const formatMonthLabelLocalized = useCallback((date: Date): string => {
    return date.toLocaleDateString(i18nLocale, { year: 'numeric', month: 'long' });
  }, [i18nLocale]);

  const formatDayHeadingLocalized = useCallback((dateKey: string): string => {
    const date = parseDateKey(dateKey);
    return date.toLocaleDateString(i18nLocale, { month: 'long', day: 'numeric', weekday: 'long' });
  }, [i18nLocale]);

  const formatTimeLabelLocalized = useCallback((event: CalendarEventItem): string => {
    if (event.all_day) return t('calendar.allDay');
    const start = parseEventDate(event.start);
    const end = parseEventDate(event.end);
    if (!start || !end) return '';
    return `${start.toLocaleTimeString(i18nLocale, { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString(i18nLocale, { hour: '2-digit', minute: '2-digit' })}`;
  }, [t, i18nLocale]);

  const localizedFailureDetails = useCallback((
    results: Array<{ provider: CalendarProviderId; success: boolean; error?: string | null }>,
  ): string | null => {
    const failed = results.filter((item) => !item.success);
    if (failed.length === 0) return null;
    const details = failed.slice(0, 2).map((item) => {
      const providerLabel = getProviderLabel(item.provider);
      return item.error?.trim()
        ? `${providerLabel}: ${item.error.trim()}`
        : t('calendar.providerFailed', { provider: providerLabel });
    }).join(' / ');
    return failed.length > 2 ? t('calendar.andMoreFailures', { details, count: failed.length - 2 }) : details;
  }, [t, getProviderLabel]);

  const buildInviteTextLocalized = useCallback((event: CalendarEventItem): string => {
    const lines: string[] = [t('calendar.invitePrefix', { title: event.title })];
    const timeLabel = event.all_day
      ? t('calendar.allDay')
      : `${event.start.replace('T', ' ').slice(0, 16)} ~ ${event.end.replace('T', ' ').slice(0, 16)}`;
    lines.push(t('calendar.inviteDateTime', { time: timeLabel }));
    if (event.location) {
      lines.push(t('calendar.inviteLocation', { location: event.location }));
    }
    if (event.meeting_url) {
      lines.push(t('calendar.inviteConference', { url: event.meeting_url }));
    }
    if (event.description) {
      lines.push('', event.description);
    }
    return lines.join('\n');
  }, [t]);

  const updateDraft = useCallback((partial: Partial<CalendarDraft>) => {
    setDraft((prev) => ({ ...prev, ...partial }));
  }, []);

  const toggleDraftProvider = useCallback((providerId: CalendarProviderId) => {
    const provider = providerInfos.find((item) => item.id === providerId);
    if (!provider || !isProviderCreatable(provider)) return;
    setDraft((prev) => {
      const exists = prev.targetProviders.includes(providerId);
      const next = exists
        ? prev.targetProviders.filter((item) => item !== providerId)
        : [...prev.targetProviders, providerId];
      return { ...prev, targetProviders: next };
    });
  }, [providerInfos]);

  const addGuestToDraft = useCallback((guest: CalendarGuest) => {
    const normalized = normalizeGuest(guest);
    if (!normalized) {
      addToast({ type: 'error', title: t('calendar.enterValidEmail') });
      return false;
    }
    let added = false;
    setDraft((prev) => {
      if (prev.guests.some((item) => item.email.toLowerCase() === normalized.email.toLowerCase())) {
        return prev;
      }
      added = true;
      return { ...prev, guests: [...prev.guests, normalized] };
    });
    setGuestQuery('');
    setDebouncedGuestQuery('');
    setGuestDropdownOpen(false);
    return added;
  }, [addToast, t]);

  const removeGuestFromDraft = useCallback((email: string) => {
    setDraft((prev) => ({
      ...prev,
      guests: prev.guests.filter((guest) => guest.email.toLowerCase() !== email.toLowerCase()),
    }));
  }, []);

  const commitGuestQuery = useCallback(() => {
    const value = guestQuery.trim();
    if (!value) return;
    if (isValidEmail(value)) {
      addGuestToDraft({ email: value });
      return;
    }
    const picked = guestSuggestions.find((suggestion) => {
      const label = `${suggestion.name || ''} ${suggestion.email}`.toLowerCase();
      return label === value.toLowerCase();
    });
    if (picked) {
      addGuestToDraft({ email: picked.email, name: picked.name || undefined });
    }
  }, [addGuestToDraft, guestQuery, guestSuggestions]);

  const resetDraft = useCallback(() => {
    if (editingEvent) {
      setDraft(draftFromEvent(editingEvent));
      return;
    }
    setDraft(defaultDraft(safeSelectedDate, preferredDraftTargets));
  }, [safeSelectedDate, preferredDraftTargets, editingEvent]);

  const openComposer = useCallback(() => {
    setEditingEvent(null);
    setComposerOpen(true);
    setCompactView('composer');
    setGuestQuery('');
    setDebouncedGuestQuery('');
    setGuestDropdownOpen(false);
    setHoveredDateKey(null);
    setPinnedPreviewDateKey(null);
    setDayPopoverPosition(null);
    setDraft(defaultDraft(safeSelectedDate, preferredDraftTargets));
  }, [safeSelectedDate, preferredDraftTargets]);

  const openEditComposer = useCallback((event: CalendarEventItem) => {
    setEditingEvent(event);
    setComposerOpen(true);
    setCompactView('composer');
    setGuestQuery('');
    setDebouncedGuestQuery('');
    setGuestDropdownOpen(false);
    setHoveredDateKey(null);
    setPinnedPreviewDateKey(null);
    setDayPopoverPosition(null);
    setDraft(draftFromEvent(event));
    onSelectedDateChange?.(enumerateEventDateKeys(event)[0] || safeSelectedDate);
  }, [onSelectedDateChange, safeSelectedDate]);

  const closeComposer = useCallback(() => {
    setComposerOpen(false);
    setEditingEvent(null);
    setCompactView('month');
    setGuestQuery('');
    setDebouncedGuestQuery('');
    setGuestDropdownOpen(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!draft.title.trim()) {
      addToast({ type: 'error', title: t('calendar.enterTitle') });
      return;
    }
    if (!draft.startDate || !draft.endDate) {
      addToast({ type: 'error', title: t('calendar.enterDate') });
      return;
    }
    if (!draft.allDay && (!draft.startTime || !draft.endTime)) {
      addToast({ type: 'error', title: t('calendar.enterTime') });
      return;
    }
    let pendingGuest: CalendarGuest | null = null;
    if (guestQuery.trim()) {
      if (isValidEmail(guestQuery.trim())) {
        pendingGuest = { email: guestQuery.trim() };
      } else {
        const picked = guestSuggestions.find((suggestion) => {
          const label = `${suggestion.name || ''} ${suggestion.email}`.toLowerCase();
          return label === guestQuery.trim().toLowerCase();
        });
        if (picked) {
          pendingGuest = { email: picked.email, name: picked.name || undefined };
        } else {
          addToast({ type: 'error', title: t('calendar.checkGuestEmail') });
          return;
        }
      }
    }

    const normalizedGuests = dedupeGuests(
      pendingGuest ? [...draft.guests, pendingGuest] : draft.guests,
    );
    if (draft.sendInviteEmail && normalizedGuests.length === 0) {
      addToast({ type: 'error', title: t('calendar.addGuestsForInvite') });
      return;
    }
    if (
      draft.sendInviteEmail
      && needsTemplateInviteSender
      && inviteSenders.length > 1
      && !draft.inviteSenderProvider
    ) {
      addToast({ type: 'error', title: t('calendar.selectInviteSender') });
      return;
    }

    const basePayload = draft.allDay
      ? {
        title: draft.title.trim(),
        start: draft.startDate,
        end: draft.endDate,
        all_day: true,
        location: draft.location.trim(),
        description: draft.description.trim(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Seoul',
      }
      : {
        title: draft.title.trim(),
        start: `${draft.startDate}T${draft.startTime}:00`,
        end: `${draft.endDate}T${draft.endTime}:00`,
        all_day: false,
        location: draft.location.trim(),
        description: draft.description.trim(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Seoul',
      };
    const calendarPayload = {
      ...basePayload,
      guests: normalizedGuests,
      send_invite_email: draft.sendInviteEmail,
      invite_sender_provider: draft.sendInviteEmail ? draft.inviteSenderProvider : null,
      conference_provider: draft.conferenceProvider,
    };

    setCreating(true);
    try {
      if (editingEvent?.group_id) {
        const result = await updateCalendarEvent(editingEvent.group_id, calendarPayload);
        const summary = summarizeCreateResult(result.results);
        const failureDetails = localizedFailureDetails(result.results);
        await queryClient.invalidateQueries({ queryKey: ['calendar'] });
        onSelectedDateChange?.(draft.startDate);
        setMonthCursor(startOfMonth(parseDateKey(draft.startDate)));

        const descriptionBase = result.results.length === 0
          ? t('calendar.eventUpdatedLocal')
          : summary.failCount > 0
            ? t('calendar.eventUpdatedPartial', { success: summary.successCount, fail: summary.failCount })
            : t('calendar.eventUpdatedAll', { count: summary.successCount });
        const inviteSuffix = result.invite_email_error
          ? ` ${t('calendar.inviteEmailFailed', { error: result.invite_email_error })}`
          : result.invite_email_sent
            ? ` ${t('calendar.inviteEmailSent')}`
            : '';
        addToast({
          type: summary.failCount > 0 || !!result.invite_email_error ? 'warning' : 'success',
          title: t('calendar.eventUpdateComplete'),
          description: `${descriptionBase}${summary.failCount > 0 && failureDetails ? ` ${failureDetails}` : ''}${inviteSuffix}`.trim(),
        });
      } else if (editingEvent) {
        const source = getMutableProviderSource(editingEvent);
        if (!source) {
          addToast({ type: 'error', title: t('calendar.noMutableEvent') });
          return;
        }
        const result = await updateCalendarProviderEvent(source.provider, source.externalId, calendarPayload);
        const summary = summarizeCreateResult(result.results);
        const failureDetails = localizedFailureDetails(result.results);
        await queryClient.invalidateQueries({ queryKey: ['calendar'] });
        onSelectedDateChange?.(draft.startDate);
        setMonthCursor(startOfMonth(parseDateKey(draft.startDate)));
        const inviteSuffix = result.invite_email_error
          ? ` ${t('calendar.inviteEmailFailed', { error: result.invite_email_error })}`
          : result.invite_email_sent
            ? ` ${t('calendar.inviteEmailSent')}`
            : '';
        addToast({
          type: summary.failCount > 0 || !!result.invite_email_error ? 'warning' : 'success',
          title: t('calendar.eventUpdateComplete'),
          description: summary.failCount > 0
            ? `${t('calendar.externalUpdatePartialFail')}${failureDetails ? ` ${failureDetails}` : ''}${inviteSuffix}`.trim()
            : `${t('calendar.externalUpdateSuccess')}${inviteSuffix}`.trim(),
        });
      } else {
        const result = await createCalendarEvents({
          ...calendarPayload,
          target_providers: draft.targetProviders,
        });
        const summary = summarizeCreateResult(result.results);
        const failureDetails = localizedFailureDetails(result.results);
        await queryClient.invalidateQueries({ queryKey: ['calendar'] });
        onSelectedDateChange?.(draft.startDate);
        setMonthCursor(startOfMonth(parseDateKey(draft.startDate)));

        if (result.success) {
          const descriptionBase = result.results.length === 0
            ? t('calendar.eventCreatedLocal')
            : summary.failCount > 0
              ? t('calendar.eventCreatedPartial', { success: summary.successCount, fail: summary.failCount })
              : t('calendar.eventCreatedAll', { count: summary.successCount });
          const inviteSuffix = result.invite_email_error
            ? ` ${t('calendar.inviteEmailFailed', { error: result.invite_email_error })}`
            : result.invite_email_sent
              ? ` ${t('calendar.inviteEmailSent')}`
              : '';
          const conferenceSuffix = result.conference_url
            ? ` ${t('calendar.conferenceLinkCreated')}`
            : '';
          addToast({
            type: summary.failCount > 0 || !!result.invite_email_error ? 'warning' : 'success',
            title: t('calendar.eventCreateComplete'),
            description: `${descriptionBase}${summary.failCount > 0 && failureDetails ? ` ${failureDetails}` : ''}${inviteSuffix}${conferenceSuffix}`.trim(),
          });
          if (result.conference_url && result.event) {
            try {
              await navigator.clipboard.writeText(buildInviteTextLocalized(result.event));
              addToast({ type: 'info', title: t('calendar.inviteCopied') });
            } catch { /* clipboard not available */ }
          }
        } else {
          addToast({
            type: 'error',
            title: t('calendar.eventCreateFailed'),
            description: failureDetails || t('calendar.eventCreateFailedDesc'),
          });
          return;
        }
      }

      resetDraft();
      setEditingEvent(null);
      setComposerOpen(false);
      setCompactView('month');
    } catch (error) {
      addToast({
        type: 'error',
        title: editingEvent ? t('calendar.eventUpdateFailed') : t('calendar.eventCreateFailed'),
        description: error instanceof Error ? error.message : t('calendar.unknownError'),
      });
    } finally {
      setCreating(false);
    }
  }, [draft, guestQuery, guestSuggestions, inviteSenders.length, needsTemplateInviteSender, addToast, queryClient, onSelectedDateChange, resetDraft, layoutMode, editingEvent, t, localizedFailureDetails, buildInviteTextLocalized]);

  const handleCopyInvite = useCallback(async (event: CalendarEventItem) => {
    try {
      await navigator.clipboard.writeText(buildInviteTextLocalized(event));
      setCopiedEventId(event.id);
      setTimeout(() => setCopiedEventId(null), 2000);
    } catch {
      addToast({ type: 'error', title: t('calendar.clipboardFailed') });
    }
  }, [addToast, t, buildInviteTextLocalized]);

  const handleDeleteEvent = useCallback(async (event: CalendarEventItem) => {
    if (!window.confirm(t('calendar.deleteConfirm', { title: event.title }))) {
      return;
    }

    setDeletingEventId(event.id);
    try {
      let result;
      if (event.group_id) {
        result = await deleteCalendarEvent(event.group_id);
      } else {
        const source = getMutableProviderSource(event);
        if (!source) {
          throw new Error(t('calendar.noMutableDeleteEvent'));
        }
        result = await deleteCalendarProviderEvent(source.provider, source.externalId);
      }
      const summary = summarizeCreateResult(result.results);
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });

      if (editingEvent?.id === event.id || (editingEvent?.group_id && editingEvent.group_id === event.group_id)) {
        setComposerOpen(false);
        setEditingEvent(null);
        setCompactView('month');
      }
      setHoveredDateKey(null);
      setPinnedPreviewDateKey(null);
      setDayPopoverPosition(null);

      const description = result.results.length === 0
        ? t('calendar.eventDeletedLocal')
        : result.success
          ? t('calendar.eventDeletedAll', { count: summary.successCount })
          : t('calendar.eventDeletedPartial', { success: summary.successCount, fail: summary.failCount });
      addToast({
        type: result.success ? 'success' : 'warning',
        title: result.success ? t('calendar.eventDeleteComplete') : t('calendar.eventDeletePartial'),
        description,
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: t('calendar.eventDeleteFailed'),
        description: error instanceof Error ? error.message : t('calendar.unknownError'),
      });
    } finally {
      setDeletingEventId(null);
    }
  }, [addToast, queryClient, editingEvent, t]);

  const renderProviderIcon = useCallback((providerId: CalendarProviderId) => (
    <ProviderIcon
      src={PROVIDER_META[providerId].icon}
      alt={PROVIDER_META[providerId].label}
      width={16}
      height={16}
    />
  ), []);

  const titleProviderFilters = providerInfos.length > 0 ? (
    <ProviderFilterGroup>
      {providerInfos.map((provider) => {
        const selected = isProviderViewable(provider) && effectiveSelectedProviders.includes(provider.id);
        const providerLabel = getProviderLabel(provider.id);
        const tooltipLabel = !provider.connected
          ? t('calendar.providerNotConnected', { provider: providerLabel })
          : provider.can_view
          ? (
              selected
                ? t('calendar.providerShowing', { provider: providerLabel })
                : t('calendar.providerHidden', { provider: providerLabel })
            )
          : t('calendar.providerCheckPermission', { provider: providerLabel });

        return (
          <Tooltip key={provider.id} label={tooltipLabel} position="bottom">
            <ProviderFilterButton
              type="button"
              $connected={isProviderViewable(provider)}
              $selected={selected}
              onClick={() => (isProviderViewable(provider) ? toggleProviderFilter(provider.id) : handleConnectCalendarProvider(provider.id))}
              aria-label={provider.label}
            >
              {renderProviderIcon(provider.id)}
            </ProviderFilterButton>
          </Tooltip>
        );
      })}
    </ProviderFilterGroup>
  ) : null;

  const renderDayPreviewPopover = () => {
    if (!activeDesktopPreviewDateKey || !dayPopoverPosition) return null;

    const previewEvents = eventsByDate.get(activeDesktopPreviewDateKey) ?? [];
    const pinned = pinnedPreviewDateKey === activeDesktopPreviewDateKey;
    if (previewEvents.length === 0 && !pinned) return null;

    return (
      <DayPreviewPopover
        style={{
          top: dayPopoverPosition.top,
          left: dayPopoverPosition.left,
          width: dayPopoverPosition.width,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DayPreviewHeader>
          <DayPreviewTitleGroup>
            <DayPreviewTitle>{formatDayHeadingLocalized(activeDesktopPreviewDateKey)}</DayPreviewTitle>
            <DayPreviewSubtitle>{t('calendar.eventCount', { count: previewEvents.length })}</DayPreviewSubtitle>
          </DayPreviewTitleGroup>
          {pinned && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setPinnedPreviewDateKey(null);
                setHoveredDateKey(null);
                setDayPopoverPosition(null);
              }}
            >
              <X size={14} />
            </Button>
          )}
        </DayPreviewHeader>
        <DayPreviewBody>
          {previewEvents.length === 0 ? (
            <AgendaEmpty>{t('calendar.noEventsOnDate')}</AgendaEmpty>
          ) : (
            previewEvents.map((event) => renderEventCard(event))
          )}
        </DayPreviewBody>
      </DayPreviewPopover>
    );
  };

  const renderCalendarGrid = () => (
    <CalendarCard
      ref={calendarCardRef}
      onMouseLeave={() => {
        if (!pinnedPreviewDateKey) {
          setHoveredDateKey(null);
          setDayPopoverPosition(null);
        }
      }}
    >
      <WeekHeader>
        {WEEKDAY_LABELS.map((key) => (
          <WeekdayCell key={key}>{t(`calendar.weekday.${key}`)}</WeekdayCell>
        ))}
      </WeekHeader>
      <MonthGrid ref={monthGridRef}>
        {monthCells.map((cell) => {
          const cellKey = toDateKey(cell);
          const isCurrentMonth = cell.getMonth() === monthCursor.getMonth();
          const isSelected = cellKey === safeSelectedDate;
          const isToday = cellKey === toDateKey(new Date());
          const dayEvents = eventsByDate.get(cellKey) ?? [];
          return (
            <DayCell
              key={cellKey}
              type="button"
              ref={(node) => {
                dayCellRefs.current[cellKey] = node;
              }}
              $selected={isSelected}
              $muted={!isCurrentMonth}
              onMouseEnter={() => {
                if (layoutMode === 'compact' || composerOpen || pinnedPreviewDateKey) return;
                if (dayEvents.length === 0) {
                  setHoveredDateKey(null);
                  setDayPopoverPosition(null);
                  return;
                }
                setHoveredDateKey(cellKey);
                updateDayPopoverPosition(cellKey);
              }}
              onClick={() => handleDateSelect(cellKey)}
            >
              <DayCellHead>
                <DayNumber $today={isToday}>{cell.getDate()}</DayNumber>
                {dayEvents.length > 0 && <DayCount>{dayEvents.length}</DayCount>}
              </DayCellHead>
              <DayEventStack>
                {dayEvents.slice(0, 1).map((event) => (
                  <DayEventPill key={`${cellKey}-${event.id}`}>
                    <DayEventDot $provider={event.provider} $localOnly={event.local_only} />
                    <span>{event.title}</span>
                  </DayEventPill>
                ))}
                {dayEvents.length > 1 && <MoreLabel>{t('calendar.moreEvents', { count: dayEvents.length - 1 })}</MoreLabel>}
              </DayEventStack>
            </DayCell>
          );
        })}
      </MonthGrid>
      {renderDayPreviewPopover()}
    </CalendarCard>
  );

  const renderEventCard = (event: CalendarEventItem) => {
    const description = normalizeCalendarText(event.description);
    return (
      <EventCard key={event.id}>
        <EventCardHead>
          <EventTitleBlock>
            <EventTitle>{event.title}</EventTitle>
            <EventTime>{formatTimeLabelLocalized(event)}</EventTime>
          </EventTitleBlock>
          <EventActionGroup>
            {!event.readonly && (event.group_id || getMutableProviderSource(event)) && (
              <Tooltip label={t('calendar.edit')} position="bottom">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openEditComposer(event)}
                >
                  <Pencil size={15} />
                </Button>
              </Tooltip>
            )}
            <Tooltip label={copiedEventId === event.id ? t('calendar.copied') : t('calendar.copy')} position="bottom">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => void handleCopyInvite(event)}
              >
                {copiedEventId === event.id ? <Check size={15} /> : <Copy size={15} />}
              </Button>
            </Tooltip>
            {!event.readonly && (event.group_id || getMutableProviderSource(event)) && (
              <Tooltip label={t('calendar.delete')} position="bottom">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => void handleDeleteEvent(event)}
                  disabled={deletingEventId === event.id}
                >
                  <Trash2 size={15} />
                </Button>
              </Tooltip>
            )}
          </EventActionGroup>
        </EventCardHead>
        <SourceList>
          {event.sources.length > 0 ? event.sources.map((source) => (
            <SourceRow key={`${event.id}-${source.provider}-${source.external_id}`}>
              <SourceLabel>
                {renderProviderIcon(source.provider)}
                <span>{getProviderLabel(source.provider)}</span>
              </SourceLabel>
              <SourceActions>
                {source.external_url && (
                  <Chip
                    clickable
                    size="sm"
                    onClick={() => window.open(source.external_url!, '_blank', 'noopener,noreferrer')}
                  >
                    {t('calendar.goToCalendar')}
                  </Chip>
                )}
                {source.meeting_url && (
                  <Chip
                    clickable
                    size="sm"
                    onClick={() => window.open(source.meeting_url!, '_blank', 'noopener,noreferrer')}
                  >
                    {t('calendar.goToMeeting')}
                  </Chip>
                )}
              </SourceActions>
            </SourceRow>
          )) : event.badges.map((badge) => (
            <SourceRow key={`${event.id}-badge-${badge}`}>
              <SourceLabel>
                <span>{badge}</span>
              </SourceLabel>
              <SourceActions />
            </SourceRow>
          ))}
        </SourceList>
        {event.location && <EventMetaText>{event.location}</EventMetaText>}
        {description && <EventDescription>{description}</EventDescription>}
      </EventCard>
    );
  };

  const renderAgendaPanel = (compact = false) => (
    <AgendaPane $compact={compact}>
      <AgendaHeader>
        <AgendaTitleGroup>
          <AgendaTitle>{formatDayHeadingLocalized(safeSelectedDate)}</AgendaTitle>
          <AgendaSubtitle>{t('calendar.eventCount', { count: selectedDayEvents.length })}</AgendaSubtitle>
        </AgendaTitleGroup>
        <div />
      </AgendaHeader>

      <AgendaScrollArea>
        <AgendaList>
          {selectedDayEvents.length === 0 ? (
            <AgendaEmpty>{t('calendar.noEventsOnDate')}</AgendaEmpty>
          ) : (
            selectedDayEvents.map((event) => renderEventCard(event))
          )}
        </AgendaList>
      </AgendaScrollArea>
    </AgendaPane>
  );

  const renderComposerForm = () => (
    <ComposerForm>
      <FormField>
        <FieldLabel>{t('calendar.fieldTitle')}</FieldLabel>
        <Input
          value={draft.title}
          onChange={(e) => updateDraft({ title: e.target.value })}
          placeholder={t('calendar.titlePlaceholder')}
        />
      </FormField>

      <FormField>
        <FieldLabel>{t('calendar.fieldConference')}</FieldLabel>
        <ConferenceOptionList>
          {availableConferenceOptions.map((option) => {
            const providerInfo = option.requiredProvider ? providerInfos.find((p) => p.id === option.requiredProvider) : null;
            const connected = option.id === 'none' || (providerInfo?.connected ?? false);
            const selected = draft.conferenceProvider === option.id;
            return (
              <ConferenceOptionRow key={option.id} $disabled={!connected}>
                <ConferenceOptionMain>
                  <Chip
                    clickable
                    size="md"
                    active={selected}
                    onClick={() => {
                      if (!connected) return;
                      const next = selected ? 'none' : option.id;
                      const updates: Partial<CalendarDraft> = { conferenceProvider: next };
                      if (next === 'zoom' && draft.allDay) {
                        updates.allDay = false;
                      }
                      updateDraft(updates);
                    }}
                    style={{ opacity: connected ? 1 : 0.45, cursor: connected ? 'pointer' : 'default' }}
                  >
                    {option.icon && (
                      <ConferenceOptionIcon src={option.icon} alt="" />
                    )}
                    {option.id === 'none' ? t('calendar.conferenceNone') : option.label}
                  </Chip>
                </ConferenceOptionMain>
                {!connected && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleGoToPlugin(option.requiredProvider ?? undefined)}
                  >
                    {t('calendar.connectAction')}
                  </Button>
                )}
              </ConferenceOptionRow>
            );
          })}
        </ConferenceOptionList>
      </FormField>

      <InlineToggle>
        <Checkbox
          checked={draft.allDay}
          onChange={(checked) => {
            const updates: Partial<CalendarDraft> = { allDay: checked };
            if (checked && draft.conferenceProvider === 'zoom') {
              updates.conferenceProvider = 'none';
            }
            updateDraft(updates);
          }}
          label={t('calendar.allDayEvent')}
          size="sm"
        />
      </InlineToggle>

      <DateGrid>
        <FormField>
          <FieldLabel>{t('calendar.startDate')}</FieldLabel>
          <CalendarDateTimeInput
            type="date"
            value={draft.startDate}
            onChange={(e) => updateDraft({ startDate: e.target.value })}
          />
        </FormField>
        <FormField>
          <FieldLabel>{t('calendar.endDate')}</FieldLabel>
          <CalendarDateTimeInput
            type="date"
            value={draft.endDate}
            onChange={(e) => updateDraft({ endDate: e.target.value })}
          />
        </FormField>
        {!draft.allDay && (
          <>
            <FormField>
              <FieldLabel>{t('calendar.startTime')}</FieldLabel>
              <CalendarDateTimeInput
                type="time"
                value={draft.startTime}
                onChange={(e) => updateDraft({ startTime: e.target.value })}
              />
            </FormField>
            <FormField>
              <FieldLabel>{t('calendar.endTime')}</FieldLabel>
              <CalendarDateTimeInput
                type="time"
                value={draft.endTime}
                onChange={(e) => updateDraft({ endTime: e.target.value })}
              />
            </FormField>
          </>
        )}
      </DateGrid>

      <FormField>
        <FieldLabel>{t('calendar.fieldLocation')}</FieldLabel>
        <Input
          value={draft.location}
          onChange={(e) => updateDraft({ location: e.target.value })}
          placeholder={t('calendar.locationPlaceholder')}
        />
      </FormField>

      <FormField>
        <FieldLabel>{t('calendar.fieldDescription')}</FieldLabel>
        <Textarea
          value={draft.description}
          onChange={(e) => updateDraft({ description: e.target.value })}
          placeholder={t('calendar.descriptionPlaceholder')}
          minRows={4}
        />
      </FormField>

      <>
        <FormField>
          <FieldLabel>{t('calendar.addGuests')}</FieldLabel>
          <GuestPicker ref={guestPickerRef}>
            {draft.guests.length > 0 && (
              <GuestChipList>
                {draft.guests.map((guest) => (
                  <GuestChip key={guest.email}>
                    <span>{guestLabel(guest)}</span>
                    <button
                      type="button"
                      onClick={() => removeGuestFromDraft(guest.email)}
                      aria-label={t('calendar.removeGuest', { email: guest.email })}
                    >
                      <X size={12} />
                    </button>
                  </GuestChip>
                ))}
              </GuestChipList>
            )}
            <Input
              variant="search"
              value={guestQuery}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              name="calendar-guest-search"
              onChange={(e) => {
                setGuestQuery(e.target.value);
                setGuestDropdownOpen(true);
              }}
              onFocus={() => setGuestDropdownOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  commitGuestQuery();
                } else if (e.key === 'Backspace' && !guestQuery && draft.guests.length > 0) {
                  removeGuestFromDraft(draft.guests[draft.guests.length - 1].email);
                }
              }}
              placeholder={t('calendar.guestSearchPlaceholder')}
            />
            {guestDropdownOpen && (
              <GuestSuggestionList>
                {guestSuggestionsLoading && (
                  <GuestSuggestionEmpty>{t('calendar.searching')}</GuestSuggestionEmpty>
                )}
                {!guestSuggestionsLoading && guestSuggestions.length === 0 && guestSuggestionProviderErrors.length > 0 && (
                  <GuestSuggestionErrorList>
                    {guestSuggestionProviderErrors.map((error) => (
                      <GuestSuggestionErrorCard key={`${error.plugin_id}-${error.message || 'none'}`}>
                        <div>
                          <GuestSuggestionErrorTitle>
                            {GUEST_SUGGESTION_PLUGIN_LABELS[error.plugin_id] || error.plugin_id}
                          </GuestSuggestionErrorTitle>
                          <GuestSuggestionErrorMessage>
                            {error.message || t('calendar.guestSearchPermission')}
                          </GuestSuggestionErrorMessage>
                        </div>
                        {error.reauth_required && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleGoToPlugin(error.plugin_id)}
                          >
                            {t('calendar.reconnect')}
                          </Button>
                        )}
                      </GuestSuggestionErrorCard>
                    ))}
                  </GuestSuggestionErrorList>
                )}
                {!guestSuggestionsLoading && guestSuggestions.length === 0 && guestQuery.trim().length > 0 && (
                  <GuestSuggestionEmpty>{t('calendar.noGuestResults')}</GuestSuggestionEmpty>
                )}
                {guestSuggestions.map((suggestion) => (
                  <GuestSuggestionButton
                    key={`${suggestion.provider}-${suggestion.email}`}
                    type="button"
                    onClick={() => addGuestToDraft({ email: suggestion.email, name: suggestion.name || undefined })}
                  >
                    <GuestSuggestionPrimary>
                      {suggestion.name || suggestion.email}
                    </GuestSuggestionPrimary>
                    {suggestion.name && <GuestSuggestionSecondary>{suggestion.email}</GuestSuggestionSecondary>}
                  </GuestSuggestionButton>
                ))}
              </GuestSuggestionList>
            )}
          </GuestPicker>
        </FormField>

        <InlineToggle>
          <Checkbox
            checked={draft.sendInviteEmail}
            onChange={(checked) => updateDraft({ sendInviteEmail: checked })}
            label={t('calendar.sendInviteEmail')}
            size="sm"
          />
        </InlineToggle>

        {draft.sendInviteEmail && needsTemplateInviteSender && (
          <FormField>
            <FieldLabel>{t('calendar.inviteSenderAccount')}</FieldLabel>
            {inviteSenders.length === 0 ? (
              <InviteHelperText>{t('calendar.connectMailForInvite')}</InviteHelperText>
            ) : (
              <InviteSenderRow>
                {inviteSenders.map((sender) => (
                  <InviteSenderChip
                    key={`${sender.provider}-${sender.email}`}
                    clickable
                    size="md"
                    active={draft.inviteSenderProvider === sender.provider}
                    onClick={() => updateDraft({ inviteSenderProvider: sender.provider })}
                  >
                    {sender.label}
                  </InviteSenderChip>
                ))}
              </InviteSenderRow>
            )}
          </FormField>
        )}

        {draft.sendInviteEmail && !needsTemplateInviteSender && (
          <InviteHelperText>{t('calendar.guestSyncNote')}</InviteHelperText>
        )}

        {draft.guests.length > 0 && hasOutlookGuestSyncTarget && (
          <InviteHelperText>{t('calendar.outlookGuestSyncNote')}</InviteHelperText>
        )}
      </>

      {!editingEvent && (
        <FormField>
          <FieldLabel>{t('calendar.syncTargetApps')}</FieldLabel>
          <TargetProviderList>
            {providerInfos.map((provider) => {
              const checked = draft.targetProviders.includes(provider.id);
              const canCreate = isProviderCreatable(provider);
              const isConnecting = connectingProviderId === provider.id;
              const actionLabel = isConnecting
                ? t('calendar.connecting')
                : provider.connected
                  ? (provider.reauth_required ? t('calendar.reconnect') : t('calendar.verify'))
                  : t('calendar.connectAction');
              return (
                <TargetProviderRow key={provider.id} $disabled={!canCreate}>
                  <TargetProviderMain>
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleDraftProvider(provider.id)}
                      disabled={!canCreate}
                      label={
                        <TargetProviderLabel>
                          {renderProviderIcon(provider.id)}
                          <span>{provider.label}</span>
                        </TargetProviderLabel>
                      }
                      size="sm"
                    />
                    {provider.connected && !provider.can_create && provider.create_error && (
                      <TargetProviderHelperText>{provider.create_error}</TargetProviderHelperText>
                    )}
                  </TargetProviderMain>
                  {!canCreate && (
                    <Button
                      variant="ghost"
                      size="sm"
                      isLoading={isConnecting}
                      disabled={isConnecting}
                      onClick={() => handleConnectCalendarProvider(provider.id)}
                    >
                      {actionLabel}
                    </Button>
                  )}
                </TargetProviderRow>
              );
            })}
          </TargetProviderList>
        </FormField>
      )}

      <ComposerActions>
        <CalendarAccentButton variant="secondary" size="md" onClick={resetDraft}>
          {editingEvent ? t('calendar.revert') : t('calendar.reset')}
        </CalendarAccentButton>
        <CalendarAccentButton
          variant="primary"
          size="md"
          isLoading={creating}
          onClick={handleSubmit}
        >
          {editingEvent ? t('calendar.saveEvent') : t('calendar.createEvent')}
        </CalendarAccentButton>
      </ComposerActions>
    </ComposerForm>
  );

  const renderCompactContent = () => (
    <CompactTrack $view={compactView}>
      <CompactPane>
        {layoutMode === 'compact' ? (
          renderCalendarGrid()
        ) : (
          <CalendarPane>
            {renderCalendarGrid()}
          </CalendarPane>
        )}
      </CompactPane>
      <CompactPane>
        <CompactHeader>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCompactView('month')}
          >
            <ArrowLeft size={16} />
          </Button>
          <CompactTitle>{formatDayHeadingLocalized(safeSelectedDate)}</CompactTitle>
          <div />
        </CompactHeader>
        {renderAgendaPanel(true)}
      </CompactPane>
      <CompactPane>
        <CompactHeader>
          <Button
            variant="ghost"
            size="icon"
            onClick={closeComposer}
          >
            <ArrowLeft size={16} />
          </Button>
          <CompactTitle>{editingEvent ? t('calendar.editEvent') : t('calendar.newEvent')}</CompactTitle>
          <div />
        </CompactHeader>
        <CompactComposerBody>
          {renderComposerForm()}
        </CompactComposerBody>
      </CompactPane>
    </CompactTrack>
  );

  const providerLoading = providersQuery.isFetching && !providersQuery.data;
  const eventLoading = eventsQuery.isFetching && !eventsQuery.data;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('calendar.title')}
      titleIcon={<Calendar size={16} />}
      titleSuffix={titleProviderFilters}
      width={`${modalDefaultWidth}px`}
      defaultHeight={modalDefaultHeight}
      minWidth={modalMinWidth}
      minHeight={modalMinHeight}
      resizable
      draggable={modalDraggable}
      pinnable={modalPinnable}
      defaultPinned={modalDefaultPinned}
      initialOffsetX={modalInitialOffsetX}
      initialOffsetY={modalInitialOffsetY}
      opacityAdjustable={modalOpacityAdjustable}
      defaultOpacity={modalDefaultOpacity}
      closeOnBackdrop={modalCloseOnBackdrop}
      dimBackdrop={modalDimBackdrop}
      passthroughBackdrop={modalPassthroughBackdrop}
      centerPaddingLeft={modalCenterPaddingLeft}
      centerPaddingRight={modalCenterPaddingRight}
      mountMode={mountMode}
      hostBounds={hostBounds}
      onSizeChange={modalOnSizeChange}
      onOffsetChange={modalOnOffsetChange}
      onPinnedChange={modalOnPinnedChange}
      onOpacityChange={modalOnOpacityChange}
      layerZIndex={modalLayerZIndex}
      onActivate={modalOnActivate}
      bodyPadding="0"
      bodyOverflowY="hidden"
      hideBodyScrollbar
    >
      <ModalBody
        ref={layoutRef}
        style={{ ['--calendar-ui-opacity' as '--calendar-ui-opacity']: String(surfaceOpacity) }}
      >
        <TopBar>
          <MonthNav>
            <Button variant="ghost" size="icon" onClick={() => setMonthCursor((prev) => addMonths(prev, -1))}>
              <ChevronLeft size={16} />
            </Button>
            <MonthLabel>{formatMonthLabelLocalized(monthCursor)}</MonthLabel>
            <Button variant="ghost" size="icon" onClick={() => setMonthCursor((prev) => addMonths(prev, 1))}>
              <ChevronRight size={16} />
            </Button>
          </MonthNav>
          <TopActions>
            <CalendarAccentButton
              variant="primary"
              size="sm"
              leftIcon={<Plus size={15} />}
              onClick={openComposer}
            >
              {t('calendar.addEvent')}
            </CalendarAccentButton>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              title={t('calendar.refresh')}
            >
              <RefreshCw size={16} className={providersQuery.isFetching || eventsQuery.isFetching ? 'spin' : undefined} />
            </Button>
          </TopActions>
        </TopBar>

        {providerLoading || eventLoading ? (
          <LoadingWrap><RingSpinner size={28} /></LoadingWrap>
        ) : (
          <ContentArea>
            {renderCompactContent()}
          </ContentArea>
        )}
      </ModalBody>
    </Modal>
  );
};

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  margin: 0;
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 16px 12px;
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
`;

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const MonthLabel = styled.div`
  min-width: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
`;

const TopActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;

  .spin {
    animation: calendar-spin 0.9s linear infinite;
  }

  @keyframes calendar-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const CalendarAccentButton = styled(Button)`
  && {
    border: 1px solid ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255,255,255, calc(0.10 + (0.10 * var(--calendar-ui-opacity, 1))))'
        : 'rgba(0,0,0, calc(0.06 + (0.06 * var(--calendar-ui-opacity, 1))))'};
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255,255,255, calc(0.05 + (0.07 * var(--calendar-ui-opacity, 1))))'
        : 'rgba(255,255,255, calc(0.72 + (0.16 * var(--calendar-ui-opacity, 1))))'};
    color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.92)' : theme.colors.textPrimary)};
    box-shadow: none;
  }

  &&:hover:not(:disabled) {
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255,255,255, calc(0.08 + (0.09 * var(--calendar-ui-opacity, 1))))'
        : 'rgba(255,255,255, calc(0.78 + (0.16 * var(--calendar-ui-opacity, 1))))'};
    border-color: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255,255,255, calc(0.14 + (0.10 * var(--calendar-ui-opacity, 1))))'
        : 'rgba(0,0,0, calc(0.08 + (0.08 * var(--calendar-ui-opacity, 1))))'};
    box-shadow: none;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const LoadingWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProviderFilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ProviderFilterButton = styled.button<{ $connected: boolean; $selected: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme, $selected }) =>
    $selected
      ? theme.colors.accent
      : (theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')};
  background: ${({ theme, $selected }) =>
    $selected
      ? (theme.mode === 'dark'
        ? 'rgb(59 130 246 / calc(0.16 * var(--calendar-ui-opacity, 1)))'
        : 'rgb(59 130 246 / calc(0.10 * var(--calendar-ui-opacity, 1)))')
      : (theme.mode === 'dark'
        ? 'rgb(255 255 255 / calc(0.04 * var(--calendar-ui-opacity, 1)))'
        : 'rgb(0 0 0 / calc(0.03 * var(--calendar-ui-opacity, 1)))')};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme, $selected }) =>
      $selected
        ? (theme.mode === 'dark'
          ? 'rgb(59 130 246 / calc(0.20 * var(--calendar-ui-opacity, 1)))'
          : 'rgb(59 130 246 / calc(0.14 * var(--calendar-ui-opacity, 1)))')
        : (theme.mode === 'dark'
          ? 'rgb(255 255 255 / calc(0.08 * var(--calendar-ui-opacity, 1)))'
          : 'rgb(0 0 0 / calc(0.05 * var(--calendar-ui-opacity, 1)))')};
  }

  img {
    opacity: ${({ $connected, $selected }) => ($connected ? ($selected ? 1 : 0.52) : 0.32)};
    filter: ${({ $connected }) => ($connected ? 'none' : 'grayscale(1)')};
  }
`;

const ProviderIcon = styled.img`
  object-fit: contain;
  width: 16px;
  height: 16px;
  transition: opacity ${({ theme }) => theme.transitions.fast}, filter ${({ theme }) => theme.transitions.fast};
`;

const CalendarPane = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  padding: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CalendarCard = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(255 255 255 / calc(0.04 * var(--calendar-ui-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.88 * var(--calendar-ui-opacity, 1)))'};
  overflow: hidden;
`;

const DayPreviewPopover = styled.div`
  position: absolute;
  z-index: 12;
  max-width: calc(100vw - 96px);
  max-height: min(320px, calc(100% - 24px));
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(20 20 22 / calc(0.94 * var(--calendar-ui-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.96 * var(--calendar-ui-opacity, 1)))'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 20px 48px rgba(0,0,0,0.35)'
      : '0 18px 40px rgba(0,0,0,0.14)'};
  backdrop-filter: blur(18px) saturate(150%);
  -webkit-backdrop-filter: blur(18px) saturate(150%);
  overflow: hidden;
`;

const DayPreviewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 12px 10px;
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
`;

const DayPreviewTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const DayPreviewTitle = styled.div`
  font-size: 0.84rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const DayPreviewSubtitle = styled.div`
  font-size: 0.74rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const DayPreviewBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;

  ${hideScrollbarStyles}
`;

const WeekHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  padding: 10px 12px;
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
`;

const WeekdayCell = styled.div`
  text-align: center;
  font-size: 0.74rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const MonthGrid = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  grid-auto-rows: minmax(94px, 1fr);
  overflow: auto;
  overscroll-behavior: contain;

  ${hideScrollbarStyles}
`;

const DayCell = styled.button<{ $selected: boolean; $muted: boolean }>`
  border: none;
  padding: 10px 10px 8px;
  background: ${({ theme, $selected }) =>
    $selected
      ? (theme.mode === 'dark' ? 'rgba(59,130,246,0.18)' : 'rgba(59,130,246,0.08)')
      : 'transparent'};
  border-right: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  color: ${({ theme, $muted }) => ($muted ? theme.colors.textMuted : theme.colors.textPrimary)};
  transition: background ${({ theme }) => theme.transitions.fast};
  min-width: 0;

  &:hover {
    background: ${({ theme, $selected }) =>
      $selected
        ? (theme.mode === 'dark' ? 'rgba(59,130,246,0.22)' : 'rgba(59,130,246,0.12)')
        : (theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)')};
  }
`;

const DayCellHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const DayNumber = styled.div<{ $today: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${({ theme, $today }) => ($today ? theme.colors.accent : 'transparent')};
  color: ${({ theme, $today }) => ($today ? theme.colors.textInverse : theme.colors.textPrimary)};
`;

const DayCount = styled.div`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const DayEventStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const DayEventPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: 4px 6px;
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(255 255 255 / calc(0.06 * var(--calendar-ui-opacity, 1)))'
      : 'rgb(0 0 0 / calc(0.04 * var(--calendar-ui-opacity, 1)))'};

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const DayEventDot = styled.span<{ $provider: CalendarProviderId; $localOnly?: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $provider, $localOnly }) => (
    $localOnly
      ? '#7C3AED'
      : $provider === 'zoom'
        ? '#2D8CFF'
        : $provider === 'google_calendar'
          ? '#34A853'
          : $provider === 'outlook_calendar'
            ? '#0078D4'
            : '#7C3AED'
  )};
`;

const MoreLabel = styled.div`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const AgendaPane = styled.div<{ $compact: boolean }>`
  width: ${({ $compact }) => ($compact ? '100%' : '360px')};
  min-width: ${({ $compact }) => ($compact ? '100%' : '320px')};
  max-width: ${({ $compact }) => ($compact ? '100%' : '420px')};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  border-left: ${({ $compact, theme }) =>
    $compact
      ? 'none'
      : `0.5px solid ${theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(255 255 255 / calc(0.02 * var(--calendar-ui-opacity, 1)))'
      : 'rgb(0 0 0 / calc(0.02 * var(--calendar-ui-opacity, 1)))'};
  padding: ${({ $compact }) => ($compact ? '12px 16px 16px' : '16px')};
  overflow: hidden;
`;

const AgendaHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
`;

const AgendaTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AgendaTitle = styled.div`
  font-size: 0.92rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const AgendaSubtitle = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const AgendaScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 2px;

  ${hideScrollbarStyles}
`;

const ComposerCard = styled.div`
  padding: 14px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.84)'};
`;

const ComposerCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
`;

const ComposerTitle = styled.div`
  font-size: 0.86rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const AgendaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AgendaEmpty = styled.div`
  padding: 24px 4px;
  text-align: center;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const EventCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(255 255 255 / calc(0.04 * var(--calendar-ui-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.82 * var(--calendar-ui-opacity, 1)))'};
`;

const EventCardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const EventActionGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  margin-top: -4px;
`;

const EventTitleBlock = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const EventTitle = styled.div`
  font-size: 0.84rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const EventTime = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ProviderBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
`;

const SourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SourceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 28px;
`;

const SourceLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.74rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 0;
`;

const SourceActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const EventMetaText = styled.div`
  font-size: 0.76rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EventDescription = styled.div`
  font-size: 0.76rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: pre-wrap;
  word-break: break-word;
`;

const EventLink = styled.a`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.accent};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ComposerForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 0.76rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const InlineToggle = styled.div`
  display: flex;
  align-items: center;
`;

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const CalendarDateTimeInput = styled(Input)`
  input[type='date'],
  input[type='time'] {
    color-scheme: ${({ theme }) => theme.mode};
  }

  input[type='date']::-webkit-calendar-picker-indicator,
  input[type='time']::-webkit-calendar-picker-indicator {
    opacity: ${({ theme }) => (theme.mode === 'dark' ? 0.94 : 0.72)};
    /* brightness(0) + invert(1) forces the native glyph to pure white in
     * dark mode regardless of the browser's color-scheme rendering.
     * (A plain invert(1) double-inverted the already-light dark-scheme
     * indicator back to black, making it invisible.) */
    filter: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'brightness(0) invert(1)'
        : 'none'};
    cursor: pointer;
  }

  input[type='date']:hover::-webkit-calendar-picker-indicator,
  input[type='time']:hover::-webkit-calendar-picker-indicator,
  input[type='date']:focus::-webkit-calendar-picker-indicator,
  input[type='time']:focus::-webkit-calendar-picker-indicator {
    opacity: ${({ theme }) => (theme.mode === 'dark' ? 1 : 0.9)};
  }
`;

const GuestPicker = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GuestChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const GuestChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.75rem;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: ${({ theme }) => theme.colors.textMuted};
    padding: 0;
    cursor: pointer;
  }
`;

const GuestSuggestionList = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};
  /* Suggestion popover must stay fully opaque — it is NOT subject to the
   * modal opacity slider (--calendar-ui-opacity). Otherwise the form
   * fields underneath (sync targets, footer actions) bleed through and
   * collide visually with the suggestion rows. */
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgb(28 28 30)' : 'rgb(255 255 255)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 16px 32px rgba(0,0,0,0.45)'
      : '0 14px 28px rgba(0,0,0,0.16)'};
`;

const GuestSuggestionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 10px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

const GuestSuggestionPrimary = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const GuestSuggestionSecondary = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const GuestSuggestionEmpty = styled.div`
  padding: 10px 12px;
  font-size: 0.74rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const GuestSuggestionErrorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GuestSuggestionErrorCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
`;

const GuestSuggestionErrorTitle = styled.div`
  font-size: 0.76rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const GuestSuggestionErrorMessage = styled.div`
  margin-top: 2px;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const InviteSenderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const InviteSenderChip = styled(Chip)`
  min-height: 32px;
  padding: 7px 14px;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 12px;
`;

const InviteHelperText = styled.div`
  font-size: 0.74rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ConferenceOptionList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const ConferenceOptionRow = styled.div<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  opacity: ${({ $disabled }) => ($disabled ? 0.64 : 1)};
`;

const ConferenceOptionMain = styled.div`
  display: inline-flex;
  align-items: center;
`;

const ConferenceOptionIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 4px;
  border-radius: 3px;
  flex-shrink: 0;
`;

const TargetProviderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TargetProviderRow = styled.div<{ $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(255 255 255 / calc(0.03 * var(--calendar-ui-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.72 * var(--calendar-ui-opacity, 1)))'};
  opacity: ${({ $disabled }) => ($disabled ? 0.64 : 1)};
`;

const TargetProviderMain = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
`;

const TargetProviderLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const TargetProviderHelperText = styled.div`
  padding-left: 28px;
  font-size: 0.72rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ComposerActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
`;

const CompactTrack = styled.div<{ $view: CompactView }>`
  display: flex;
  width: 300%;
  height: 100%;
  transform: translateX(
    ${({ $view }) => ($view === 'month' ? '0%' : $view === 'agenda' ? '-33.3333%' : '-66.6667%')}
  );
  transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
`;

const CompactPane = styled.div`
  width: 33.3333%;
  min-width: 33.3333%;
  max-width: 33.3333%;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const CompactHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px 0;
`;

const CompactTitle = styled.div`
  font-size: 0.84rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CompactComposerBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 28px 20px;
  overscroll-behavior: contain;

  ${hideScrollbarStyles}
`;

export default CalendarWidgetModal;
